import { useGetAgencyAnyQuery } from '@saaga/shared/store/api.store'
import { AlertCircleIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'

import { AgencyCreateEditView } from './agency-create-edit.view'

const AgencyEditLoading = (): ReactNode => (
  <div className="agency-edit-loading flex h-full flex-col overflow-hidden">
    <div className="flex items-center justify-between border-b p-4">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-20" />
        <Skeleton className="h-6 w-32" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-9 w-28" />
        <Skeleton className="h-9 w-32" />
      </div>
    </div>
    <div className="flex flex-1">
      <div className="w-1/2 border-r p-6 space-y-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
      <div className="w-1/2 p-6">
        <Skeleton className="h-full w-full" />
      </div>
    </div>
  </div>
)

export const AgencyEditPage = (): ReactNode => {
  const { agencySlug } = useParams<{ agencySlug: string }>()

  const {
    data: agencyResponse,
    isLoading,
    isError,
  } = useGetAgencyAnyQuery(agencySlug ?? '', {
    skip: !agencySlug,
  })

  if (isLoading) {
    return <AgencyEditLoading />
  }

  if (isError) {
    return (
      <div className="agency-edit-error flex h-full flex-col p-6">
        <Alert variant="destructive">
          <AlertCircleIcon className="size-4" />
          <AlertTitle>Error loading agency</AlertTitle>
          <AlertDescription>
            There was a problem loading the agency for editing. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!agencyResponse) {
    return (
      <div className="agency-edit-not-found flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Agency not found</h2>
          <p className="text-muted-foreground mt-2">
            The agency you are trying to edit does not exist.
          </p>
        </div>
      </div>
    )
  }

  return <AgencyCreateEditView agencyResponse={agencyResponse} isEditMode />
}
